/**
 * Takes the credential out of a rejected request before anyone can log it.
 *
 * An AxiosError carries the config it was built from, and our request interceptor puts
 * `Authorization: Bearer <token>` there — so `console.error("...", error)` prints the user's
 * token into the console, along with the request body. Three hundred-odd call sites do exactly
 * that, and every new catch block is a chance to add another, so this is done once on the way
 * out rather than at each place that logs.
 *
 * Not a replacement for logging the message instead of the object: a printed error object is
 * still a wall of noise. It is the part that must not depend on anyone remembering.
 */

export const REDACTED = "[redacted]";

/** Axios 1.x hands over an AxiosHeaders (which normalises case through set/get), but a hand-made
 *  error — or a test — may carry a plain object, where the key can be spelled either way. */
const redactHeaders = (headers: unknown): void => {
	if (!headers || typeof headers !== "object") return;

	const withSet = headers as { set?: (name: string, value: string) => void; get?: unknown };
	if (typeof withSet.set === "function") {
		withSet.set("Authorization", REDACTED);
		return;
	}

	const plain = headers as Record<string, unknown>;
	for (const key of Object.keys(plain)) {
		if (key.toLowerCase() === "authorization") plain[key] = REDACTED;
	}
};

/** Mutates in place and returns the same error: it is rejected onwards, and callers compare
 *  identity (`instanceof`, `error.code`) on the value they were handed. */
export const redactAuthorization = <T>(error: T): T => {
	const candidate = error as { config?: unknown; response?: { config?: unknown } } | null;
	if (!candidate || typeof candidate !== "object") return error;

	redactHeaders((candidate.config as { headers?: unknown } | undefined)?.headers);
	// Usually the very same config object, but not guaranteed across axios versions.
	redactHeaders((candidate.response?.config as { headers?: unknown } | undefined)?.headers);

	return error;
};
